/**
 * Application Entry Point
 *
 * Sets up:
 * - React Query for data fetching
 * - React Router for navigation
 * - Sonner for toast notifications
 * - Global error boundary
 * - Error checker utility
 * - Firebase (Analytics, Messaging, Performance, Error Reporting), loaded after the page
 *
 * Public pages arrive prerendered (scripts/prerender.mjs); React hydrates that
 * HTML instead of throwing it away and drawing it again.
 */

import React from "react";
import { captureAcquisition } from "./lib/acquisition";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import AppRouter from "./routes/AppRouter";
import { Site } from "./Site";
import { initMetaPixel } from "./lib/metaPixel";
import { GlobalErrorBoundary } from "./components/ErrorBoundary";
import { hasAccessToken } from "./lib/authSession";
import { IS_ONPREM } from "./lib/deployment";
import "./index.css";

// Firebase (~120 KB) is not needed to show a page: load it once the page is up.
const loadFirebase = () => import("./config/firebase");
const reportError = (error: Error, context?: Record<string, unknown>) =>
  loadFirebase()
    .then((m) => m.reportError(error, context))
    .catch(() => {});
// On-prem the Firebase project is ours, not the customer's: never load it.
if (!IS_ONPREM) {
  window.addEventListener("load", () => {
    loadFirebase()
      .then((m) => m.initializeFirebase())
      .catch((err) => console.warn("[firebase] load failed:", err));
  });
}

// Register the service worker for PWA install + offline shell.
// The SW file also handles Firebase background messaging — same scope,
// same file (see public/firebase-messaging-sw.js). Only register in
// production builds; HMR + a controlling SW make local dev painful.
if (import.meta.env.PROD && !IS_ONPREM && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js", { scope: "/" })
      .catch((err) => console.warn("[PWA] SW register failed:", err));
  });
}

// Pages and the app load in chunks. A tab opened before a deploy asks for
// chunk files the new build no longer has; reload once to pick up the new one.
window.addEventListener("vite:preloadError", (event) => {
  try {
    if (sessionStorage.getItem("humo.chunkReload")) return;
    sessionStorage.setItem("humo.chunkReload", "1");
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

// Initialize error checker in development (or when switched on in storage)
try {
  if (import.meta.env.DEV || localStorage.getItem("enableErrorChecker")) {
    import("./utils/errorChecker").then((m) => m.initErrorChecker());
  }
} catch {
  // storage unavailable: no error checker
}

// Global error handlers for uncaught errors
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error);
  reportError(event.error || new Error(event.message), {
    context: 'window.onerror',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  reportError(
    event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
    {
      context: 'unhandledrejection',
      promise: 'Promise rejection not caught'
    }
  );
});

// Configure React Query with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry failed requests once
      retry: 1,
      // Consider data stale after 30 seconds
      staleTime: 30000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Don't refetch on window focus by default
      refetchOnWindowFocus: false,
      // Show loading state for at least 100ms to prevent flicker
    },
    mutations: {
      // Don't retry mutations by default
      retry: 0
    }
  }
});

// First touch wins: remember the campaign before any navigation rewrites the URL.
captureAcquisition();

// Meta Pixel: no-op without VITE_META_PIXEL_ID or marketing consent.
initMetaPixel();

const app = (
  <React.StrictMode>
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Site>
            <AppRouter />
          </Site>
        </BrowserRouter>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>
);

const container = document.getElementById("root")!;

/**
 * The prerender is the Turkish page for a logged-out visitor. Hydrate only
 * when this visit would render the same thing; otherwise draw from scratch
 * (a saved English choice, or a logged-in user sent on from "/").
 */
function matchesPrerender(): boolean {
  if (!container.firstElementChild) return false;
  // On-prem "/" goes to the login page, not the prerendered landing.
  if (IS_ONPREM) return false;
  try {
    const language = localStorage.getItem("language");
    if (language && language !== "tr") return false;
    if (window.location.pathname === "/" && hasAccessToken()) return false;
  } catch {
    return false;
  }
  return true;
}

if (matchesPrerender()) {
  ReactDOM.hydrateRoot(container, app);
} else {
  ReactDOM.createRoot(container).render(app);
}
